import { wrapLines } from '../../primitives/layout';
import { createRenderProgram } from '../../artifacts/RenderProgram';
import { commitRenderProgram } from '../../artifacts/interpreter';

export async function prepareTextNode(env, spec) {
  const area = env.boardArea();
  const safeInset = Math.max(12, Math.min(area.w, area.h) * 0.03);
  const textArea = {
    x: area.x + safeInset,
    y: area.y + safeInset,
    w: Math.max(1, area.w - safeInset * 2),
    h: Math.max(1, area.h - safeInset * 2),
  };
  const baseFontSize = env.getStyle(
    spec.style.size === 'title'
      ? 'titleSize'
      : spec.style.size === 'body'
        ? 'bodySize'
        : spec.style.size === 'mono'
          ? 'codeSize'
          : 'smallSize'
  );
  const baseLineHeight = env.getStyle('lineHeight');
  let fontSize = baseFontSize;
  let lineHeight = baseLineHeight;

  const fontFamily =
    spec.style.font === 'mono'
      ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
      : 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial';

  const measureFn = (text) => {
    return env.measureText(text, fontSize).width;
  };

  let lines = wrapLines(spec.text, textArea.w, measureFn);
  const minFontSize = Math.max(10, Math.round(baseFontSize * 0.62));
  const fitIterations = 10;
  for (let i = 0; i < fitIterations; i++) {
    const blockHeight = lines.length * lineHeight;
    const fits = blockHeight <= textArea.h;
    if (fits || fontSize <= minFontSize) break;

    // Shrink typography progressively to fit long narrative text within the visible canvas.
    const scale = Math.max(0.84, Math.min(0.95, Math.sqrt(textArea.h / Math.max(1, blockHeight))));
    fontSize = Math.max(minFontSize, fontSize * scale);
    lineHeight = Math.max(fontSize * 1.2, lineHeight * scale);
    lines = wrapLines(spec.text, textArea.w, measureFn);
  }
  const totalChars = lines.join('').length;

  const blockHeight = lines.length * lineHeight;
  const vAlign = spec.style.vAlign || 'center';
  const yStart =
    vAlign === 'top'
      ? textArea.y + fontSize + 2
      : vAlign === 'bottom'
        ? textArea.y + textArea.h - blockHeight + fontSize
        : textArea.y + (textArea.h - blockHeight) / 2 + fontSize * 0.3;

  const highlights = [];
  if (spec.highlights) {
    for (const hl of spec.highlights) {
      let charCount = 0;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const matchIndex = line.indexOf(hl.match);
        if (matchIndex >= 0) {
          const beforeMatch = line.substring(0, matchIndex);
          const x = textArea.x + measureFn(beforeMatch);
          const width = measureFn(hl.match);
          const y = yStart + i * lineHeight;

          highlights.push({
            lineIndex: i,
            charStart: charCount + matchIndex,
            charEnd: charCount + matchIndex + hl.match.length,
            x,
            y,
            width,
            mode: hl.mode,
            triggerTime: hl.startSec,
            duration: hl.durSec,
          });
        }
        charCount += line.length;
      }
    }
  }

  const elements = [
    {
      id: 'text',
      renderer: 'chalkTextBlock',
      params: {
        lines,
        layout: {
          x: textArea.x,
          yStart,
          lineHeight,
          areaX: textArea.x,
          areaY: textArea.y,
          areaW: textArea.w,
          areaH: textArea.h,
        },
        totalChars,
        fontSize,
        fontFamily,
        justify: spec.style.justify,
        highlights,
      },
    },
  ];

  const tracks = [
    {
      id: 'textTimeline',
      anim: 'textTimeline',
      target: 'text',
      clock: 'local',
      params: {
        charsPerSec: spec.typewriter.charsPerSec,
        totalChars,
        showCursor: spec.typewriter.cursor,
        holdSec: 0.5,
        highlights,
      },
    },
  ];

  const artifacts = Array.isArray(spec.artifacts) ? spec.artifacts : [];
  artifacts.forEach((artifact, index) => {
    const artifactId = `artifact_${artifact.id || index}`;
    elements.push({
      id: artifactId,
      renderer: 'primitive2d',
      params: {
        area,
        primitive: artifact.primitive,
        ...(artifact.params || {}),
      },
    });

    if (artifact.motion?.type) {
      tracks.push({
        id: `artifact_motion_${index}`,
        anim: 'kinematics2d',
        target: artifactId,
        clock: 'local',
        params: {
          ...artifact.motion,
        },
      });
    }
  });

  return {
    renderProgram: createRenderProgram({
      elements,
      tracks,
      clear: {
        x: area.x,
        y: area.y,
        w: area.w,
        h: area.h,
      },
      meta: {
        durationSec: totalChars / Math.max(1, spec.typewriter.charsPerSec) + 0.5,
      },
    }),
  };
}

export function commitTextNode(packet, ctxs, anim) {
  commitRenderProgram(packet, ctxs, anim);
}
